import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, Clock } from "lucide-react";
import { useEffect, useState } from "react";

export default function OtpModal({ open, onOpenChange, otp, setOtp, onVerify, resendOTP, loading, email }) {

    const [expiresIn, setExpiresIn] = useState(60); // 2 minutes
    const timerRef = React.useRef(null);

    const startTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        setExpiresIn(60);

        timerRef.current = setInterval(() => {
            setExpiresIn((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        if (!open) return;

        startTimer();

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [open]);


    const minutes = String(Math.floor(expiresIn / 60)).padStart(2, "0");
    const seconds = String(expiresIn % 60).padStart(2, "0");
    const OTP_LENGTH = 6;
    const inputRefs = React.useRef([]);

    const handleOtpChange = (e, index) => {
        const value = e.target.value.replace(/\D/g, "");
        if (!value) {
            updateOtp(index, "");
            return;
        }

        if (value.length > 1) {
            const chars = value.slice(0, OTP_LENGTH - index).split("");
            chars.forEach((char, i) => updateOtp(index + i, char));
            const next = Math.min(index + chars.length, OTP_LENGTH - 1);
            inputRefs.current[next]?.focus();
            return;
        }

        updateOtp(index, value);
        if (index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const updateOtp = (index, value) => {
        const next = otp.split("");
        next[index] = value;
        setOtp(next.join(""));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-none bg-background shadow-2xl">
                <div className="relative h-2 w-full bg-gradient-to-r from-primary via-primary/80 to-emerald-400">
                    <div className="absolute inset-0 opacity-60 bg-gradient-to-r from-primary via-primary/70 to-emerald-400" />
                </div>

                <div className="p-6 space-y-6">
                    <DialogHeader className="space-y-3 items-center text-center">
                        <div className="relative">
                            <div className="absolute inset-0 rounded-full" />
                            <div className="relative rounded-full bg-primary/15 p-3 ring-1 ring-primary/25 shadow-sm">
                                <ShieldCheck className="h-5 w-5 text-primary" />
                            </div>
                        </div>
                        <DialogTitle className="text-lg font-semibold">
                            Secure verification
                        </DialogTitle>
                     <p className="text-xs text-muted-foreground leading-relaxed text-center">
  Enter the OTP sent to{" "}
  <span className="font-medium text-primary break-all">{email}</span>.
  <br />
  <span className="text-[11px] text-muted-foreground">
    The code is valid for 5 minutes.
  </span>
</p>

                    </DialogHeader>

                    <div className="flex flex-col items-center">
                        <div className="flex gap-3">
                            {Array.from({ length: 6 }).map((_, index) => (
                                <input className=" h-11 w-11 rounded-lg border border-input bg-background text-center text-lg font-mono font-bold shadow-md transition-all focus:outline-none focus:ring-1 focus:ring-primary focus:scale-105"
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={otp[index] || ""}
                                    onChange={(e) => handleOtpChange(e, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    autoFocus={index === 0}
                                    autoComplete="one-time-code"
                                />
                            ))}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <Button className="w-full h-11 font-medium shadow-lg transition-all active:scale-[0.97]"
                            disabled={otp.length !== 6 || loading} onClick={onVerify}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Verifying…
                                </>
                            ) : (
                                "Verify & continue"
                            )}
                        </Button>

                        <button className="block mx-auto text-xs text-primary font-medium disabled:cursor-not-allowed disabled:text-muted-foreground"
                            onClick={async () => {
                                try {
                                    startTimer();
                                    await resendOTP();
                                } catch (err) {
                                    console.error("Resend OTP failed", err);
                                }
                            }}
                            disabled={expiresIn > 0}>
                            Resend code  {expiresIn > 0 && (<b>in {minutes}:{seconds}</b>)}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
